import type { AssetType } from "./types";

/**
 * Price and currency formatting.
 *
 * Ported from legacy/services/services.js. Crypto prices span many orders of
 * magnitude — a single trade list can hold both $64,000 (BTC) and $0.00000012
 * (a meme coin) — so precision is chosen per-value rather than fixed.
 */

export interface PreferredCurrency {
  label: string;
  value: string;
  unit: string;
}

export const preferredCurrencies: readonly PreferredCurrency[] = [
  { label: "USD", value: "usd", unit: "$" },
  { label: "EUR", value: "eur", unit: "€" },
  { label: "British Pound", value: "gbp", unit: "£" },
  { label: "Japanese Yen", value: "jpy", unit: "¥" },
  { label: "Canadian Dollar", value: "cad", unit: "CAD" },
  { label: "Bitcoin", value: "btc", unit: "₿" },
];

/** Significant decimal places to show for a value of this magnitude. */
export function decimalsForMagnitude(value = 0): number {
  const abs = Math.abs(value);
  if (abs < 0.001) return 8;
  if (abs < 0.01) return 6;
  if (abs < 1) return 4;
  if (abs < 10) return 3;
  if (abs < 50) return 2;
  return 0;
}

/**
 * How many decimal places a number actually carries.
 *
 * The 2020 version did `value.toString().split(".")[1].length`, which threw a
 * TypeError on any value JavaScript renders in exponential notation —
 * (1e-7).toString() is "1e-7", so there is no "." to split on. Crypto prices
 * reach that magnitude routinely.
 */
export function countDecimals(value: number): number {
  if (!Number.isFinite(value) || Number.isInteger(value)) return 0;

  const text = value.toString();
  if (text.includes("e")) {
    const [mantissa = "", exponent = "0"] = text.split("e");
    const mantissaDecimals = mantissa.split(".")[1]?.length ?? 0;
    return Math.max(0, mantissaDecimals - Number(exponent));
  }

  return text.split(".")[1]?.length ?? 0;
}

/**
 * Round a price to a sensible number of decimals for its magnitude.
 *
 * Always returns a number. The 2020 version returned a *string* when an
 * explicit `decimalPlaces` was passed (`toFixed`) but a number otherwise
 * (`parseFloat`), so callers doing arithmetic on the result silently got
 * string concatenation instead of addition.
 */
export function formatPrice(price: number | null | undefined, decimalPlaces?: number): number | null {
  if (price === 0) return 0;
  if (price == null || !Number.isFinite(price)) return null;

  const decimals = decimalPlaces ?? decimalsForMagnitude(price);
  return Number.parseFloat(price.toFixed(decimals));
}

/**
 * Round a price the way its own market quotes it.
 *
 * `decimalsForMagnitude` is tuned for crypto, where a list can hold both
 * $64,000 and $0.00000012 and cents are noise at the top of that range — so it
 * drops to zero decimals above 50. Equities are quoted in cents at every price,
 * and a stock bought at 214.53 displayed as "215" is simply the wrong number.
 */
export function formatAssetPrice(
  price: number | null | undefined,
  assetType: AssetType,
): number | null {
  return formatPrice(price, assetType === "stock" ? 2 : undefined);
}

/**
 * Format a figure that was captured at write time, in the currency it was
 * actually captured in.
 *
 * Stored figures are final — historical non-USD rates cannot be re-fetched on
 * the free tier — so a trade recorded while the preference was EUR still holds
 * euros after the preference changes to USD. Formatting it with the *current*
 * preference would relabel it without converting it, which is the one outcome
 * worse than showing a mixed-currency total.
 *
 * `storedCode` is absent on documents written before it was recorded. Those
 * cannot be disambiguated, so the current preference is the best available
 * guess and the dashboard says as much rather than the figure claiming it.
 */
export function formatStoredFigure(
  value: number | null | undefined,
  storedCode: string | undefined,
  currentPreference: string,
): string {
  return formatPreferredCurrency(value, storedCode ?? currentPreference);
}

/**
 * Symbols for the currencies a trade can be quoted in.
 *
 * The dollar-pegged stablecoins share `$` deliberately: a USDC price is a
 * dollar price to anyone reading it, and "USDC1,234" reads as a part number.
 * Anything unlisted falls back to the code after the figure, which is why this
 * map does not need to be exhaustive.
 */
const QUOTE_SYMBOLS: Record<string, string> = {
  USD: "$",
  USDT: "$",
  USDC: "$",
  EUR: "€",
  GBP: "£",
  JPY: "¥",
  CAD: "CA$",
  BTC: "₿",
  ETH: "Ξ",
};

/**
 * A price as it should be *read* — grouped, signed, and carrying its currency.
 *
 * Distinct from `formatPrice`, which returns a **number** for arithmetic and
 * comparison. That distinction is load-bearing: the 2020 version returned a
 * string from one branch and a number from the other, so callers doing sums on
 * the result silently got concatenation. This one is display-only and always
 * returns a string, so the two cannot be confused at a call site.
 *
 * Grouping is the visible part — `62993` is a number you have to count digits
 * on, `$62,993` is a price you read at a glance.
 */
export function formatQuotePrice(
  price: number | null | undefined,
  quoteCurrency: string,
  assetType: AssetType = "crypto",
): string {
  if (price == null || !Number.isFinite(price)) return "—";

  const code = quoteCurrency.toUpperCase();
  const isStock = assetType === "stock";

  const amount = new Intl.NumberFormat("en-US", {
    // Equities are quoted in cents at every price, so 214.5 shows as 214.50.
    // Crypto keeps magnitude-based precision and no padding: a coin worth
    // $0.00000012 needs eight places, and $62,993 needs none.
    minimumFractionDigits: isStock ? 2 : 0,
    maximumFractionDigits: isStock ? 2 : decimalsForMagnitude(price),
  }).format(Math.abs(price));

  const symbol = QUOTE_SYMBOLS[code];
  const body = symbol ? `${symbol}${amount}` : `${amount} ${code}`;

  // The sign leads, ahead of the unit — `$-50` reads as a typo. Same rule as
  // `formatPreferredCurrency`; profit figures reach this function too.
  return price < 0 ? `-${body}` : body;
}

/**
 * Format a value with its currency symbol, e.g. `$1234.56`, `₿0.00042`, `-$50`.
 *
 * The sign leads, ahead of the unit. Concatenating unit then number gives
 * `$-50`, which reads as a typo — and a losing portfolio total is the first
 * place that shows, since per-trade profit on the older screens was only ever
 * displayed for winners.
 */
export function formatPreferredCurrency(value: number | null | undefined, currency: string): string {
  const match = preferredCurrencies.find((c) => c.value === currency);
  // The 2020 version destructured `.unit` off an unguarded `.find()`, throwing
  // a TypeError for any currency outside the list.
  const unit = match?.unit ?? "";

  // BTC amounts need magnitude-based precision; fiat is conventionally 2dp.
  const decimals = currency === "btc" ? undefined : 2;
  const amount = value === 0 ? 0 : (formatPrice(value, decimals) ?? 0);

  return amount < 0 ? `-${unit}${Math.abs(amount)}` : `${unit}${amount}`;
}
